import type { ComponentType } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { Layout } from '@/layout/Layout';
import { Foutscherm, NaarFabriek, Omlijst, Opstarten, Root, SpelRoute } from '@/layout/routes';
import { UserLogin } from '@/screens/Login/UserLogin';
import { AlleenSpelleider } from '@/screens/Spelleider/AlleenSpelleider';

/**
 * Laadt een scherm pas als de route nodig is, zodat elk scherm een eigen chunk krijgt en de
 * eerste download klein blijft. De schermbestanden hebben een standaardexport.
 */
function scherm(laad: () => Promise<{ default: ComponentType }>) {
  return async () => ({ Component: (await laad()).default });
}

/**
 * De routes van `App.tsx` uit het origineel, met dezelfde paden en parameternamen.
 *
 * React Router 8 in data-modus: de routes staan buiten de React-boom en de router wordt
 * één keer gemaakt. `RouterProvider` komt uit 'react-router/dom', zoals v8 voorschrijft
 * voor de browser (die versie gebruikt `flushSync` van react-dom).
 *
 * Anders dan in het origineel heeft de spelerroute geen `/*` meer: kinderen onder een
 * route met een splat matchen in v8 niet. Wat de schermen uit `useParams()['*']` haalden,
 * geeft nu `useSchermPad()`.
 */
const router = createBrowserRouter([
  {
    Component: Root,
    HydrateFallback: Opstarten,
    ErrorBoundary: Foutscherm,
    children: [
      { path: '/', Component: UserLogin },
      // Niet in het origineel: de highscores, voor iedereen. Een vast pad gaat voor `/:gameId`.
      { path: '/highscores', lazy: scherm(() => import('@/screens/Highscores')) },
      // Niet in het origineel: de spelleider logt in. Deze drie schermen werken zonder
      // sessie; ze komen uit de links in de mails en van het inlogscherm.
      {
        path: '/admin/wachtwoord-vergeten',
        lazy: scherm(() => import('@/screens/Spelleider/WachtwoordVergeten')),
      },
      {
        path: '/admin/wachtwoord-herstellen/:token',
        lazy: scherm(() => import('@/screens/Spelleider/WachtwoordHerstellen')),
      },
      {
        path: '/admin/uitnodiging/:token',
        lazy: scherm(() => import('@/screens/Spelleider/Uitnodiging')),
      },
      {
        // Zonder sessie toont `AlleenSpelleider` hier het inlogscherm, op dezelfde URL. Het
        // spel zelf hangt eronder, zodat de WebSocket pas na het inloggen opent.
        path: '/admin',
        Component: AlleenSpelleider,
        children: [
          {
            index: true,
            lazy: async () => ({
              Component: (await import('@/screens/Login/AdminCreateGame')).AdminCreateGame,
            }),
          },
          { path: 'spelleiders', lazy: scherm(() => import('@/screens/Spelleider/Spelleiders')) },
          {
            path: ':gameId',
            element: <SpelRoute spelleider />,
            children: [
              {
                Component: Omlijst,
                children: [{ index: true, lazy: scherm(() => import('@/screens/Admin')) }],
              },
            ],
          },
        ],
      },
      {
        path: '/:gameId/:userId',
        Component: Layout,
        children: [
          { index: true, lazy: scherm(() => import('@/screens/Factory')) },
          { path: 'taken', lazy: scherm(() => import('@/screens/Tasks')) },
          { path: 'magazijn', lazy: scherm(() => import('@/screens/Inventory')) },
          { path: 'organisatie', lazy: scherm(() => import('@/screens/Organisation')) },
          { path: 'obs', lazy: scherm(() => import('@/screens/OBS')) },
          { path: 'asset-data', lazy: scherm(() => import('@/screens/AssetData')) },
          { path: 'archief', lazy: scherm(() => import('@/screens/Archive')) },
          { path: 'assets/:asset', lazy: scherm(() => import('@/screens/Assets')) },
          { path: '*', Component: NaarFabriek },
        ],
      },
      {
        // Het gezamenlijke takenscherm, niet de login (zie screens/Tasks/SharedTaskView.tsx).
        path: '/:gameId',
        Component: SpelRoute,
        children: [{ index: true, lazy: scherm(() => import('@/screens/Tasks/SharedTaskView')) }],
      },
      // Wat nergens op past, gaat terug naar het beginscherm.
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);

export function App() {
  return <RouterProvider router={router} />;
}
